import { Button, Dialog, Icon, Sheet, useIsMobile } from '@forecastify/ui';
import { useT } from '../../i18n';
import { ListHeader, usePageHeader } from '../page-header';
import { Breadcrumb } from '../breadcrumb';
import {
  RequestDetail,
  type RequestDetailData,
  type RequestDetailHandlers,
} from './request-detail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE THREE SURFACES — drawer, modal, full page. Item 34's ladder.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A request opened from a list arrives as a **drawer**, exactly as a task does; the drawer can be
 * grown into a **modal** and the modal into a **page**. `task-surface.tsx` settled the pattern and
 * the arguments carry over verbatim:
 *
 * ⚠️ **Only the full page claims the shell's header.** The drawer's title belongs to its `Sheet`
 * and the modal's to its own 48px bar; both float over a screen whose header describes something
 * else, and taking that bar from them would relabel the list underneath.
 *
 * ⚠️ **`wide` follows the surface, not the kind.** The drawer stacks the rail under the body; the
 * modal and the page put it beside.
 */

export type RequestMode = 'drawer' | 'modal' | 'full';

export interface RequestSurfaceProps {
  readonly mode: RequestMode;
  readonly open: boolean;
  readonly data: RequestDetailData;
  readonly handlers: RequestDetailHandlers;
  readonly serverError: string | null;
  readonly onModeChange: (mode: RequestMode) => void;
  readonly onClose: () => void;
  /**
   * Where a breadcrumb segment goes. ⚠️ Absent makes the trail plain text — right for a surface
   * with nowhere to send somebody, which is every overlay opened over a list.
   */
  readonly onNavigate?: (path: string) => void;
}

/** The code and the one-line title both bars show. */
function heading(data: RequestDetailData): { code: string; title: string } {
  if (data.kind === 'leave') {
    return {
      code: data.request.identifier,
      title: `${data.request.leaveTypeName} · ${data.request.fromDate} – ${data.request.toDate}`,
    };
  }
  return {
    code: data.claim.identifier,
    /* ⚠️ The day alone until an approver says which kind of overtime it was — `0147`. */
    title:
      data.claim.overtimeTypeName === null
        ? data.claim.workedOn
        : `${data.claim.overtimeTypeName} · ${data.claim.workedOn}`,
  };
}

export function RequestSurface(props: RequestSurfaceProps) {
  const mobile = useIsMobile();
  if (props.mode === 'full') return <FullPage {...props} />;
  /* ⚠️ 17 Oct 2026 · on a phone a modal is the full-screen drawer — the product owner. */
  if (props.mode === 'modal' && !mobile) return <Modal {...props} />;
  return <Drawer {...props} />;
}

function Drawer({ open, data, handlers, serverError, onModeChange, onClose }: RequestSurfaceProps) {
  const t = useT();
  const { code } = heading(data);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={code}
      width="min(560px, 96vw)"
      closeLabel={t('common.close')}
      /* 17 Oct 2026 · no mode buttons on a phone: the drawer is the only frame there. */
      actions={
        useIsMobile()
          ? []
          : [
              {
                label: t('task.mode.modal'),
                icon: 'maximize',
                onSelect: () => {
                  onModeChange('modal');
                },
              },
            ]
      }
    >
      <RequestDetail data={data} handlers={handlers} serverError={serverError} wide={false} />
    </Sheet>
  );
}

function Modal({ open, data, handlers, serverError, onModeChange, onClose }: RequestSurfaceProps) {
  const t = useT();
  const { code, title } = heading(data);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      bare
      /* ⚠️ Item 36 · the width every detail modal in the product uses. */
      width="min(1320px, 94vw)"
      closeLabel={t('common.close')}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        <span className="text-[12.5px] tabular-nums">{code}</span>
        <span className="text-muted-foreground min-w-0 flex-1 truncate text-[12.5px]">{title}</span>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.full')}
          title={t('task.mode.full')}
          onClick={() => {
            onModeChange('full');
          }}
        >
          <Icon name="maximize" size={15} />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={15} />
        </Button>
      </div>

      {/* ⚠️ A flex column the detail FILLS rather than the scroller — 14 Oct. The detail's header
          stays put and its two halves scroll apart (`DetailFrame` in `detail-chrome.tsx`); the
          overflow is only the fallback for a body that is not bounded. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <RequestDetail data={data} handlers={handlers} serverError={serverError} wide />
      </div>
    </Dialog>
  );
}

function FullPage({
  data,
  handlers,
  serverError,
  onModeChange,
  onClose,
  onNavigate,
}: RequestSurfaceProps) {
  const t = useT();
  const { code } = heading(data);
  /* ⚠️ The TYPE beside the trail — *éves szabadság* — which is what the header adds over the
     breadcrumb itself. */
  const type = data.kind === 'leave' ? data.request.leaveTypeName : data.claim.overtimeTypeName;

  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon-sm"
          className="-ml-1.5"
          aria-label={t('leave.detail.back')}
          onClick={onClose}
        >
          <Icon name="chevron-left" size={16} />
        </Button>
        {/**
         * ┌───────────────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ THE TRAIL, LIKE EVERY OTHER DETAIL — 15 Sep.                         │
         * └───────────────────────────────────────────────────────────────────────────┘
         *
         * *"On a leave request we need to change the header like we did on the other places:
         * breadcrumb, code (copy button) | type."*
         *
         * ⚠️ **The title is gone from the header**, because it is the first thing in the body — the
         * duplication is what the report names. What the header carries instead is WHERE, which the
         * body cannot say, and the code, which is the one fact people quote at each other.
         */}
        <Breadcrumb
          copyValue={code}
          crumbs={[
            { label: t('breadcrumb.home'), onClick: () => onNavigate?.('/home') },
            { label: t('title.requests'), onClick: () => onNavigate?.('/requests') },
            { label: code },
          ]}
          trailing={
            <span className="text-muted-foreground min-w-0 truncate text-[12.5px]">{type}</span>
          }
        />
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
      </>
    ),
    /* ⚠️ **VALUES ONLY** — `page-header.tsx`'s loop. The builder reaches the callbacks by ref. */
    [code, type],
  );

  return (
    <div data-screen="request" className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* 1160px, the width every full-page detail uses. */}
        <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
          <RequestDetail data={data} handlers={handlers} serverError={serverError} wide />
        </div>
      </div>
    </div>
  );
}

/**
 * ⚠️ **A drawer opened over a list needs a header the list does not have**, which is what this is
 * for: the overlay used from `Kérelmeim`, `Kérelmek` and the Szabadságok timeline, where closing
 * returns to the list rather than navigating.
 */
export function RequestOverlayHeader({ title }: { readonly title: string }) {
  return <ListHeader title={title} />;
}
